import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isLegalIndustry } from '../../lib/industry';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Users, Settings, ArrowLeft, RefreshCw, BarChart3, Brain,
  Phone, Lightbulb, Trophy, Mail, EyeOff, FileText, RotateCcw, Layers, Scale, Download,
  Activity, FileSearch,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import AdvancedTab from '../../components/intelligence/AdvancedTab';
import GamificationTab from '../../components/intelligence/GamificationTab';
import CallerIntelTab from '../../components/intelligence/CallerIntelTab';
import MyCoachingTab from '../../components/intelligence/MyCoachingTab';
import TeamPulseTab from '../../components/intelligence/TeamPulseTab';
import ReportBuilderTab from '../../components/intelligence/ReportBuilderTab';
import GlobalRevivableDealsTab from '../../components/intelligence/GlobalRevivableDealsTab';

import { API, StatCard } from './domain/domainUtils';
import { DomainCallsTab } from './domain/DomainCallsTab';
import { DomainAnalyticsTab } from './domain/DomainAnalyticsTab';
import CoachingTab from '../../components/intelligence/CoachingTab';
import { DomainOverviewTab } from './domain/DomainOverviewTab';
import { DomainUsersTab } from './domain/DomainUsersTab';
import { DomainSubtypeBreakdownTab } from './domain/DomainSubtypeBreakdownTab';
import { DomainMasqueradeModal } from './domain/DomainMasqueradeModal';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'calls', label: 'Calls', icon: Phone },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'team-pulse', label: 'Team Pulse', icon: Activity },
  { id: 'subtypes', label: 'Sub-Intents', icon: Layers },
  { id: 'coaching', label: 'Coaching', icon: Brain },
  { id: 'intelligence', label: 'Intelligence', icon: Lightbulb },
  { id: 'revivable', label: 'Revivable Opps', icon: RefreshCw },
  { id: 'caller-intel', label: 'Caller Intel', icon: Brain },
  { id: 'agents-coaching', label: 'Agents Coaching', icon: Trophy },
  { id: 'achievements', label: 'Achievements', icon: Trophy },
  { id: 'report-builder', label: 'Report Builder', icon: FileSearch },
  { id: 'users', label: 'Users', icon: Users },
];

const DEFAULT_SCHEDULE = { enabled: false, day_of_week: 4, hour: 9, minute: 0, timezone: 'Australia/Brisbane' };

const AdminDomainDetailPage = () => {
  const { domainId } = useParams();
  const { token, startMasquerade } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [domain, setDomain] = useState(null);
  const [users, setUsers] = useState([]);
  const [emailRouting, setEmailRouting] = useState(null);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState(searchParams.get('tab') || 'overview');
  const [callsStatusFilter, setCallsStatusFilter] = useState(searchParams.get('status') || '');
  const [callsAgentFilter, setCallsAgentFilter] = useState(searchParams.get('agent_id') || '');
  const [callsCriterionFilter, setCallsCriterionFilter] = useState('');
  const [callsTimeWindow, setCallsTimeWindow] = useState(null); // { weekday, hourMin, hourMax, label }

  const [syncing, setSyncing] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [autoRetrying, setAutoRetrying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showMasquerade, setShowMasquerade] = useState(false);
  const [masquerading, setMasquerading] = useState(false);

  const changeTab = useCallback((newTab) => {
    setTab(newTab);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev); p.set('tab', newTab); return p;
    }, { replace: true });
  }, [setSearchParams]);

  const switchToCallsTab = useCallback((targetTab = 'calls', statusFilter = '', agentId = '', criterion = '', timeWindow = null) => {
    setTab(targetTab);
    if (statusFilter !== undefined) setCallsStatusFilter(statusFilter);
    if (agentId !== undefined) setCallsAgentFilter(agentId);
    if (criterion !== undefined) setCallsCriterionFilter(criterion);
    if (timeWindow !== undefined) setCallsTimeWindow(timeWindow);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, uRes, routingRes, schedRes] = await Promise.all([
        axios.get(`${API}/admin/domains/${domainId}`, { headers }),
        axios.get(`${API}/admin/domains/${domainId}/users`, { headers }),
        axios.get(`${API}/admin/domains/${domainId}/email-routing`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/domains/${domainId}/coaching-schedule`, { headers }).catch(() => ({ data: null })),
      ]);
      setDomain(dRes.data);
      setUsers(uRes.data);
      if (routingRes.data) setEmailRouting(routingRes.data);
      if (schedRes.data) setSchedule(prev => ({ ...prev, ...schedRes.data }));
    } catch (err) {
      toast.error('Failed to load domain');
      console.error('loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, [domainId, headers]);

  useEffect(() => { loadData(); }, [loadData]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/domains/${domainId}/sync`, {}, { headers });
      toast.success('Sync started — calls will appear shortly');
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleReclassify = async () => {
    if (!window.confirm(
      'Re-classify existing calls based on current extension coaching types and re-grade them with the correct knowledge base?\n\nThis may take a few minutes for large call volumes.'
    )) return;
    setReclassifying(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/reclassify`, {}, { headers });
      const { reclassified, message } = r.data;
      if (reclassified === 0) toast.info(message);
      else toast.success(`${message} Refresh Calls tab in ~1 min to see updated scores.`);
    } catch (err) {
      toast.error('Reclassify failed');
      console.error('Reclassify error:', err);
    } finally {
      setReclassifying(false);
    }
  };

  const handleSendWeeklyEmail = async () => {
    if (!window.confirm('Send AI-generated weekly coaching summary to notification emails now?')) return;
    setSendingEmail(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/send-weekly-coaching`, {}, { headers });
      const { recipients, plus_addressed, agents_in_queue, kpis } = r.data;
      const allSent = [...recipients, ...plus_addressed];
      toast.success(`Coaching email sent to ${allSent.length} address${allSent.length !== 1 ? 'es' : ''}! (${agents_in_queue} agents in queue, avg score: ${kpis.avg_score})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send coaching email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAutoRetry = async () => {
    setAutoRetrying(true);
    try {
      const r = await axios.post(`${API}/admin/jobs/run-stuck-reaper`, {}, { headers });
      toast.success(r.data.message || 'Auto-retry triggered');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-retry failed to start');
    } finally {
      setAutoRetrying(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      // Streamed CSV — fetch as blob so the browser triggers a real download
      // with the server-provided Content-Disposition filename.
      const r = await axios.get(
        `${API}/admin/domains/${domainId}/gradings/export.csv?days=90`,
        { headers, responseType: 'blob' },
      );
      const filename =
        r.headers?.['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1]
        || `${domainId}-gradings-90d.csv`;
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleMasquerade = async (targetUser) => {
    setMasquerading(true);
    try {
      const r = await axios.post(`${API}/admin/masquerade/${targetUser.id}`, {}, { headers });
      startMasquerade(r.data.user, r.data.access_token);
      setShowMasquerade(false);
      navigate('/dashboard');
      toast.success(`Now viewing as ${targetUser.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Masquerade failed');
    } finally {
      setMasquerading(false);
    }
  };

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-white/20 animate-spin" />
      </div>
    </AdminLayout>
  );

  const quickStats = [
    { label: 'Total Calls', value: domain?.total_calls || 0, color: 'text-white', onClick: () => changeTab('calls') },
    { label: 'Extensions', value: domain?.extensions_monitored?.length || 0, color: 'text-teal-400', onClick: () => changeTab('overview') },
    { label: 'Portal Users', value: users.length, color: 'text-blue-400', onClick: () => changeTab('users') },
    { label: 'Notifications', value: domain?.notification_emails?.length || 0, color: 'text-violet-400', onClick: null },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/domains')} className="text-white/30 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">{domain?.company?.name || domain?.yabbit_domain}</h1>
              <p className="text-white/40 text-sm">{domain?.yabbit_domain} · {domain?.industry?.replace(/_/g, ' ')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowMasquerade(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-sm font-medium rounded-lg transition-colors"
              data-testid="masquerade-btn"
              title="View portal as a domain user"
            >
              <EyeOff className="w-4 h-4" />
              View as User
            </button>
            <button
              onClick={() => navigate(`/admin/domains/${domainId}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-sm rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={handleSendWeeklyEmail}
              disabled={sendingEmail}
              data-testid="send-coaching-email-btn"
              title="Send AI-generated weekly coaching summary to notification emails"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {sendingEmail ? 'Sending...' : 'Email Report'}
            </button>
            <button
              onClick={() => navigate(`/admin/domains/${domainId}/portal`)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
              data-testid="view-portal-btn"
            >
              <BarChart3 className="w-4 h-4" />
              View Portal
            </button>
            <button
              onClick={handleReclassify}
              disabled={reclassifying}
              title="Re-assign coaching types to existing calls based on current extension settings"
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              data-testid="reclassify-btn"
            >
              {reclassifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
              Re-classify
            </button>
            <button
              onClick={() => navigate(`/admin/domains/${domainId}/sync-log`)}
              title="Audit trail of every CDR ingest, transcript fetch, recording capture, and grading event"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
              data-testid="view-sync-log-btn"
            >
              <FileText className="w-4 h-4" />
              Sync Log
            </button>
            <button
              onClick={handleExportCsv}
              disabled={exporting}
              title="Download a CSV of this tenant's gradings + call metadata for the last 90 days"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              data-testid="export-csv-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
            {isLegalIndustry(domain?.industry) && (
              <button
                onClick={() => navigate(`/admin/domains/${domainId}/smokeball`)}
                title="Smokeball legal-software integration: OAuth credentials, webhook URL, test connection"
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
                data-testid="smokeball-settings-btn"
              >
                <Scale className="w-4 h-4" />
                Smokeball
              </button>
            )}
            <button
              onClick={handleAutoRetry}
              disabled={autoRetrying}
              title="Trigger the stuck-call reaper now — retries every no_transcript and stuck-pending call without waiting for the hourly cron"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              data-testid="auto-retry-now-btn"
            >
              {autoRetrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Auto-Retry Now
            </button>
            <button
              onClick={triggerSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg transition-colors"
              data-testid="domain-sync-btn"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync Now
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickStats.map(s => <StatCard key={s.label} {...s} />)}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 p-1 rounded-xl w-fit flex-wrap">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => changeTab(t.id)}
              data-testid={`domain-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'
              }`}
            >
              {t.icon && <t.icon className="w-3.5 h-3.5" />}
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'overview' && (
          <DomainOverviewTab
            domain={domain}
            domainId={domainId}
            token={token}
            onRefresh={loadData}
            onSendWeeklyEmail={handleSendWeeklyEmail}
            sendingEmail={sendingEmail}
            emailRouting={emailRouting}
            schedule={schedule}
            onAgentClick={(agentId) => switchToCallsTab('calls', '', agentId)}
            onViewAnalytics={() => changeTab('analytics')}
          />
        )}
        {tab === 'calls' && (
          <DomainCallsTab
            domainId={domainId}
            token={token}
            initialStatusFilter={callsStatusFilter}
            agentFilter={callsAgentFilter}
            criterionFilter={callsCriterionFilter}
            timeWindow={callsTimeWindow}
          />
        )}
        {tab === 'analytics' && (
          <DomainAnalyticsTab domainId={domainId} token={token} onSwitchTab={switchToCallsTab} />
        )}
        {tab === 'subtypes' && (
          <DomainSubtypeBreakdownTab domainId={domainId} />
        )}
        {tab === 'coaching' && (
          <CoachingTab
            domainId={domainId}
            onAgentClick={(agentId) => switchToCallsTab('calls', '', agentId)}
          />
        )}
        {tab === 'intelligence' && (
          <div className="bg-white rounded-xl p-6">
            <AdvancedTab
              domainId={domainId}
              onCriteriaClick={(topic) => switchToCallsTab('calls', '', '', topic)}
              onVolumeCellClick={(win) => switchToCallsTab('calls', '', '', '', win)}
            />
          </div>
        )}
        {tab === 'revivable' && (
          <div className="bg-white rounded-xl p-6">
            <GlobalRevivableDealsTab domainId={domainId} />
          </div>
        )}
        {tab === 'caller-intel' && (
          <div className="bg-white rounded-xl p-6">
            <CallerIntelTab domainId={domainId} />
          </div>
        )}
        {tab === 'agents-coaching' && (
          <div className="bg-white rounded-xl p-6">
            <MyCoachingTab domainId={domainId} />
          </div>
        )}
        {tab === 'achievements' && (
          <div className="bg-white rounded-xl p-6">
            <GamificationTab domainId={domainId} />
          </div>
        )}
        {tab === 'team-pulse' && (
          <div className="bg-white rounded-xl p-6">
            <TeamPulseTab domainId={domainId} />
          </div>
        )}
        {tab === 'report-builder' && (
          <div className="bg-white rounded-xl p-6">
            <ReportBuilderTab domainId={domainId} />
          </div>
        )}
        {tab === 'users' && (
          <DomainUsersTab
            domainId={domainId}
            domain={domain}
            users={users}
            setUsers={setUsers}
            token={token}
          />
        )}
      </div>

      {showMasquerade && (
        <DomainMasqueradeModal
          users={users}
          masquerading={masquerading}
          onSelect={handleMasquerade}
          onClose={() => setShowMasquerade(false)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminDomainDetailPage;
